import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@/app/container';
import { UsersRepositoryToken } from '../../di/tokens';
import type {
  PerkGrantBatchAddItem,
  PerkGrantBatchRevokeItem,
} from '../../domain/entities/user.entity';

/** Perks concedidos a um platformProfile. */
export function useProfilePerks(profileId: string) {
  const repository = container.resolve(UsersRepositoryToken);
  return useQuery({
    queryKey: ['profile-perks', profileId],
    queryFn: () => repository.listGrantedPerks(profileId),
    enabled: !!profileId,
  });
}

/**
 * O que o perfil de fato tem, com a linhagem dos derivados. A key começa por
 * `['profile-perks', id]` de propósito: a invalidação depois do batch pega as
 * duas queries — mudar concessão muda o fecho.
 */
export function useEffectivePerks(profileId: string, scopeId: string) {
  const repository = container.resolve(UsersRepositoryToken);
  return useQuery({
    queryKey: ['profile-perks', profileId, 'effective', scopeId],
    queryFn: () => repository.listEffectivePerks(profileId, scopeId),
    enabled: !!profileId && !!scopeId,
  });
}

interface ApplyPerkGrantBatchVariables {
  grant: PerkGrantBatchAddItem[];
  revoke: PerkGrantBatchRevokeItem[];
}

/**
 * Aplica o diff inteiro do perfil — concessões e revogações no mesmo commit. O
 * `platformProfileId` vem do hook, não das variáveis: quem chama já está preso
 * a um perfil.
 */
export function useApplyPerkGrantBatch(profileId: string) {
  const queryClient = useQueryClient();
  const repository = container.resolve(UsersRepositoryToken);
  return useMutation({
    mutationFn: ({ grant, revoke }: ApplyPerkGrantBatchVariables) =>
      repository.applyPerkGrantBatch({
        platformProfileId: profileId,
        grant,
        revoke,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['profile-perks', profileId],
      });
    },
  });
}
